/*
 * @prop --component-link-color-underline: Color of link underline
 * @prop --component-link-color-icon: Color of link icon
 * @prop --component-link-color-text: Color of link text
 * @prop --component-link-color-text-hover: Color of link text on hover
 * @prop --component-link-button-color-background: Background color of link button
 * @prop --component-link-button-color-background-hover: Background color of link button on hover
 * @prop --component-link-button-color-background-active: Background color of link button in active state
 * @prop --component-link-button-color-text: Color of the text inside link button
 * @prop --component-link-button-color-text-hover: Color of the text inside link button
 * @prop --component-link-button-color-icon: Color of the icon inside link button
 * @prop --component-link-button-color-icon-hover: Color of the icon inside link button on hover
 * @prop --component-link-button-secondary-color-text: Text color of secondary link button
 * @prop --component-link-button-secondary-color-text-active: Text color of secondary link button in active state
 * @prop --component-link-button-secondary-color-border: Color of the border of secondary link button
 * @prop --component-link-button-secondary-color-border-active: Border color secondary link button in active state
 * @prop --component-link-button-secondary-color-background: Background color of secondary link button
 * @prop --component-link-button-secondary-color-background-hover: Background color of secondary link button on hover
 * @prop --component-link-button-secondary-color-icon: color of secondary link button icon
 * @prop --component-link-button-cta-color-background: Background color of cta link button
 * @prop --component-link-button-cta-color-icon: Background color of cta link button icon
 * @prop --component-link-button-cta-color-background-hover: Background color of cta link button on hover
 * @prop --component-link-button-cta-color-background-active: Background color of cta link button in active state
 * @prop --component-link-button-font-family: Font family of link button
 */

/* stylelint-disable custom-property-empty-line-before */
:host {
  display: inline-flex;
  cursor: pointer;
  max-width: 100%;
  font-family: var(--component-link-font-family, var(--skin-font-family-primary, $font-family-sans-serif));

  a {
    border: none;
    text-decoration-line: underline;
    text-decoration-color: var(--component-link-color-underline, var(--current-color-primary, $ui-blue));

    &.no-decoration {
      text-decoration: none;
    }

    ui-icon {
      text-decoration: underline;
      width: 20px;
      height: 20px;
      margin-right: 8px;
      vertical-align: text-bottom;
      --component-icon-color: var(--component-link-color-icon, var(--current-color-primary, #{$ui-blue}));
    }

    span {
      flex: 1;
      word-break: break-word;
    }
  }

  .button {
    position: relative;
    padding: 14px 16px;
    width: 100%;
    max-width: 100%;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    background-color: var(--component-link-button-color-background, var(--current-color-primary, $ui-blue));
    border: 1px solid var(--component-link-button-color-background, var(--current-color-primary, $ui-blue));
    border-radius: .25rem;
    cursor: pointer;
    font-family: var(--component-link-button-font-family, var(--skin-font-family-primary, $font-family-sans-serif));
    font-size: 1rem;
    font-weight: $font-weight-normal;
    line-height: 1.27rem; //firefox crop some letters like g with 1.25rem
    overflow: hidden;
    text-align: center;
    text-decoration: none;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--component-link-button-color-text, var(--current-color-primary-inverted, $ui-white));

    @include breakpoint($small) {
      max-width: inherit;
    }

    ui-icon {
      --component-icon-color:
        var(
          --component-link-button-color-icon,
          var(--current-color-primary-inverted, #{$ui-white})
        );
    }

    &--cta {
      background-color: var(--component-link-button-cta-color-background, var(--skin-color-accent, $ui-green));
      border-color: var(--component-link-button-cta-color-background, var(--skin-color-accent, $ui-green));
      color: var(--component-link-button-color-text, var(--skin-color-primary-inverted, $ui-white));

      ui-icon {
        --component-icon-color:
          var(
            --component-link-button-cta-color-icon,
            var(--skin-color-primary-inverted, #{$ui-white})
          );
      }
    }

    &--secondary {
      border-color: var(--component-link-button-secondary-color-border, var(--current-color-primary, $ui-blue));
      background-color: var(--component-button-secondary-color-background, transparent);
      color: var(--component-link-button-color-text, var(--current-color-primary, $ui-blue));

      ui-icon {
        --component-icon-color:
          var(
            --component-link-button-secondary-color-icon,
            var(--current-color-primary, #{$ui-blue})
          );
      }
    }
  }
}

a:not(.button) span {
  color: var(--component-link-color-text, var(--current-color-primary, $ui-blue));
}

:host(:hover) {
  a:not(.button) span {
    color: var(--component-link-color-text-hover, var(--current-color-primary-light, $ui-blue-minus-1));
  }

  .button {
    &--primary {
      border-color:
        var(
          --component-link-button-color-background-hover,
          var(--skin-color-primary-light, $ui-blue-minus-1)
        );
      background-color:
        var(
          --component-link-button-color-background-hover,
          var(--skin-color-primary-light, $ui-blue-minus-1)
        );

      span {
        color: var(--component-link-button-color-text-hover, var(--skin-color-primary-inverted, $ui-white));
      }

      ui-icon {
        --component-icon-color:
          var(
            --component-link-button-color-icon-hover,
            var(--skin-color-primary-inverted, #{$ui-white})
          );
      }
    }

    &--cta {
      background-color:
        var(
          --component-link-button-cta-color-background-hover,
          var(--skin-color-accent-light, $ui-green-minus-1)
        );
      border-color:
        var(
          --component-link-button-cta-color-background-hover,
          var(--skin-color-accent-light, $ui-green-minus-1)
        );
    }

    &--secondary {
      background-color:
        var(
          --component-button-secondary-color-background,
          var(--current-color-primary-inverted, transparent)
        );

      ::after {
        background-color:
          var(
            --component-link-button-secondary-color-background-hover,
            var(--current-color-primary, $ui-blue)
          );
        opacity: .05;
        position: absolute;
        content: '';
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }
}

:host(:active) {
  .button {
    &--primary {
      background-color:
        var(
          --component-link-button-color-background-active,
          var(--skin-color-primary-shade, $ui-blue-plus-2)
        );
      border-color:
        var(
          --component-link-button-color-background-active,
          var(--skin-color-primary-shade, $ui-blue-plus-2)
        );
    }

    &--cta {
      border-color:
        var(
          --component-link-button-cta-color-background-active,
          var(--skin-color-accent-shade, $ui-green-plus-1)
        );
      background-color:
        var(
          --component-link-button-cta-color-background-active,
          var(--skin-color-accent-shade, $ui-green-plus-1)
        );
    }

    &--secondary {
      border-color:
        var(
          --component-link-button-secondary-color-border-active,
          var(--current-color-primary-shade, $ui-blue-plus-2)
        );

      ui-icon {
        --component-icon-color:
          var(
            --component-link-button-secondary-color-icon-active,
            var(--current-color-primary-shade, #{$ui-blue-plus-2})
          );
      }

      span {
        color:
          var(
            --component-link-button-secondary-color-text-active,
            var(--current-color-primary-shade, $ui-blue-plus-2)
          );
      }
    }
  }
}

:host([icon-option='icon-only']) {
  a ui-icon {
    margin-right: 0;
  }
}

:host([conversion='dropdown']) {
  a {
    text-decoration: none;
  }
}

:host(.inherit-font-style) {
  color: inherit;
  font-size: inherit;
  line-height: inherit;

  a,
  a span {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
  }
}

:host(.full-width-xs) {
  @include breakpoint($xsmallOnly) {
    width: 100%; // button variants of ui-link should be full width in mobile
  }
}
